import type { Booking } from './types.js';
import { dateKey } from './quote.js';

// The latest end-time across a booking's linked calls, or undefined when it
// has none (a call-less/day-scoped booking). Used both to sort a person's
// history and to decide whether a booking is in the past.
export function bookingEndsAt(booking: Pick<Booking, 'linkedCalls'>): Date | undefined {
  const calls = booking.linkedCalls;
  if (!calls || calls.length === 0) return undefined;
  return calls.reduce((latest, c) => (c.endTime.getTime() > latest.getTime() ? c.endTime : latest), calls[0].endTime);
}

// Whether a booking represents work that's already happened, for splitting
// "upcoming" from "past" in My Offers and a person's history view. Only a
// booking that was actually confirmed (ACCEPTED/BOOKED/COMPLETED) can be
// "past" — a pending, declined, or cancelled booking never is, regardless
// of its dates.
//
// Prefers linkedCalls (real, absolute call instants — an exact comparison,
// no time zone involved) over scopeDate (a call-less booking's day-only
// commitment). scopeDate is authored as a specific instant (midnight in
// whichever browser wrote it), not a pure calendar date, so comparing it
// against `now` needs an explicit IANA time zone via the same dateKey(...)
// helper the cost engine uses for exactly this reason — otherwise a
// same-day booking could flip to "past" at the wrong moment depending on
// whichever time zone happens to be evaluating it, the same class of bug
// fixed for OT/day-rate bucketing elsewhere in this package.
export function isPastBooking(
  booking: Pick<Booking, 'state' | 'linkedCalls' | 'scopeDate'>,
  now: Date,
  timeZone = 'UTC',
): boolean {
  if (booking.state !== 'ACCEPTED' && booking.state !== 'BOOKED' && booking.state !== 'COMPLETED') return false;

  const endsAt = bookingEndsAt(booking);
  if (endsAt) return endsAt.getTime() < now.getTime();

  if (booking.scopeDate) return dateKey(booking.scopeDate, timeZone) < dateKey(now, timeZone);

  return false;
}
